import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Grid,
  Tooltip, 
  IconButton
} from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Iconify from '../../../reusable-components/iconify';
import { postAddItem } from '../../../api/rentals';
import { UOM_TYPES } from '../../../utils/dropdowns';
import { fNoNegativeOrLetters } from '../../../utils/formatText';
import {
  getCategoryData,
  getPricingTypeOptions,
  getRentalItems,
  getRentalVendors,
  getBaseUomTypes
} from '../../../redux/slices/rentals';
import { useDispatch, useSelector } from '../../../redux/store';
import { useSnackbar } from '../../../reusable-components/snackbar';
import { formatMoney } from '../../../utils/formatNumber';
import CloseIconButton from '../../../reusable-components/basic-buttons/CloseIconButton';

AddRentalItem.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
};

export default function AddRentalItem({ open, setOpen }) {
  const {
    data: { categoryData, rentalVendors, pricingTypes} ,
  } = useSelector((state) => state.rentals);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    description: '',
    notes: '',
    category: '',
    subcategory: '',
    vendor: '',
    vendorItemID: '',
    uomType: '',
    uomAmount: '',
    mainPrice: '',
    mainPricingType: '',
    pricingTypes: [],
    capAmount: '',
    daysToCap: '',
    vendorItemLink: '',
    imageUrl: '',
    weightLimit: '',
    warranty: ''
  });
  const dispatch = useDispatch();
  const steps = ['Item Details', 'Vendor Information', 'Additional Information']; 
  const uomTypes = useMemo(() => 
    UOM_TYPES.map((type) => type.label)
  , []);
  const handleNext = () => {
    if (isStepValid) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const formItemHeight = 38;

  const deletePricingType = (index) => {
    if (formData?.pricingTypes?.length === 1) {
      setFormData({
        ...formData,
        pricingTypes: [],
      });
    } else{
      setFormData((prevData) => ({
        ...prevData,
        pricingTypes: prevData.pricingTypes?.filter((_,i) => i !== index),
      }));
    }
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleClose = () => {
    setOpen(false);
    clearFormData();
    setActiveStep(0);
  };

  const clearFormData = () => {
    setFormData({  
    description: '',
    notes: '',
    category: '',
    subcategory: '',
    vendor: '',
    vendorItemID: '',
    uomType: '',
    uomAmount: '',
    mainPrice: '',
    mainPricingType: '',
    pricingTypes: [],
    capAmount: '',
    daysToCap: '',
    vendorItemLink: '',
    imageUrl: '', 
    weightLimit: '',
    warranty:''});}

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const isStepValid = useMemo(() => {
    switch (activeStep) {
      case 0:
        return formData?.description && formData?.category && !!formData?.subcategory;
      case 1:
        return formData?.vendor && formData?.vendorItemID && formData?.uomType && formData?.uomAmount;
      case 2:
        return formData?.mainPrice && formData?.mainPricingType;
      default:
        return false;
    }
  }, [
    activeStep,
    formData?.category,
    formData?.description,
    formData?.mainPrice,
    formData?.mainPricingType,
    formData?.subcategory,
    formData?.uomAmount,
    formData?.uomType,
    formData?.vendor,
    formData?.vendorItemID,
  ]);

  const handleAddItem = async () => {
    const pricingTypeIds = formData?.pricingTypes?.map(pt => pt.pricingType);
    const hasDuplicatePricingType = pricingTypeIds.some((id, idx) => id && pricingTypeIds.indexOf(id) !== idx || id === formData?.mainPricingType);
    if (hasDuplicatePricingType) {
      enqueueSnackbar('Duplicate pricing types.', { variant: 'error' });
      return;
    }
    const response = await postAddItem(formData);
    if (response.data.success) {
      dispatch(getRentalItems());
      enqueueSnackbar('Rental Item Added', { variant: 'success' });
      clearFormData();
      setActiveStep(0);
    } else {
      enqueueSnackbar('Error while adding item', { variant: 'error' });
      clearFormData();
      setActiveStep(0);
    }

    navigate(`/dashboard/rentals/rental-catalog`);
    setOpen(false);
  };

  const seenCategories = new Set();
  const categoryOptions = useMemo(
    () =>
      categoryData.reduce((acc, item) => {
        if (!seenCategories.has(item.categoryName)) {
          seenCategories.add(item.categoryName);
          acc.push({
            label: item.categoryName,
            id: item.categoryId,
          });
        }
        return acc;
      }, []),
    [categoryData]
  );

  const setSubcategoryOptions = () =>
    categoryData
      .filter((item) => item.categoryId === formData?.category?.id && !!item.subcategoryId)
      .map((item) => ({
        subcategoryId: item.subcategoryId,
        subcategoryName: item.subcategoryName,
      }));

  const vendorOptions = rentalVendors.map((vendor) => ({
    id: vendor.id,
    name: vendor.name,
  }));

  const updatePricingType = (index, field, value) => {
    if (index === 0 && formData?.pricingTypes?.length === 0) {
      setFormData((prevData) => ({
        ...prevData,
        pricingTypes: [{ [field]: value }],
      }));
    }
    else{
    setFormData((prevData) => ({
      ...prevData,
      pricingTypes: prevData.pricingTypes?.map((pricingType, i) =>
        i === index ? { ...pricingType, [field]: value } : pricingType
      ),
    }))}
};

  useEffect(() => {
    dispatch(getCategoryData());
    dispatch(getRentalVendors());
    dispatch(getPricingTypeOptions());
    dispatch(getBaseUomTypes());
  }, [dispatch]);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        sx={{
          textAlign: 'center',
          color: 'rgba(112, 112, 112, 0.5)',
        }}
      >
        Add New Rental Item
        <CloseIconButton onClick={handleClose}/>
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel sx={{ color: 'rgba(112, 112, 112, 0.5)', mb: 4 }}>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {activeStep === 0 && (
          <>
            <Grid container direction="row" justifyContent="space-between" alignItems="center">
              <Grid item xs={12}>
                <DialogContentText style={{ fontWeight: 'bold' }}>Description *</DialogContentText>
                <TextField
                  size="small"
                  fullWidth
                  margin="dense"
                  multiline
                  autoComplete="off"
                  value={formData?.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  sx={{ height: formItemHeight }}
                  InputProps={{ sx: { height: formItemHeight } }}
                />
              </Grid>
            </Grid>
            <Grid container direction="row" justifyContent="space-between" alignItems="center">
              <Grid item xs={12}>
                <DialogContentText style={{ fontWeight: 'bold' }}>Notes</DialogContentText>
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  margin="dense"
                  autoComplete="off"
                  value={formData?.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                />
              </Grid>
            </Grid>
            <Grid container direction="row" justifyContent="space-between" alignItems="flex-end">
              <Grid item xs={5.9}>
                <DialogContentText style={{ fontWeight: 'bold' }}>Category *</DialogContentText>
                <Autocomplete
                  options={categoryOptions}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      margin="dense"
                      sx={{ height: formItemHeight }}
                      autoComplete="off"
                      InputProps={{
                        ...params.InputProps,
                        sx: { height: formItemHeight },
                      }}
                    />
                  )}
                  getOptionLabel={(option) => option?.label || ''}
                  value={formData?.category}
                  onChange={(e, value) => {
                    setFormData({ ...formData, category: value, subcategory: null });
                  }}
                  sx={{ height: formItemHeight, mb: 4 }}
                />
              </Grid>
              <Grid item xs={5.9}>
                <DialogContentText style={{ fontWeight: 'bold' }}>Subcategory *</DialogContentText>
                <Autocomplete
                  options={setSubcategoryOptions()}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      margin="dense"
                      sx={{ height: formItemHeight, mb: 4 }}
                      autoComplete="off"
                      InputProps={{
                        ...params.InputProps,
                        sx: { height: formItemHeight },
                      }}
                    />
                  )}
                  getOptionLabel={(option) => option?.subcategoryName || ''}
                  value={formData?.subcategory}
                  onChange={(e, value) => {
                    handleInputChange('subcategory', value);
                  }}
                  noOptionsText="No subcategories found"
                  sx={{ height: formItemHeight, mb: 4 }}
                />
              </Grid>
            </Grid>
          </>
        )}
        {activeStep === 1 && (
          <>
            <Grid container direction="row" justifyContent="space-between" alignItems="flex-end">
              <Grid item xs={12}>
                <DialogContentText style={{ fontWeight: 'bold' }}>Vendor *</DialogContentText>
                <Autocomplete
                  fullWidth
                  options={vendorOptions}
                  getOptionLabel={(option) => option.name || ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      size="small"
                      autoComplete="off"
                      sx={{ height: formItemHeight }}
                      InputProps={{
                        ...params.InputProps,
                        sx: { height: formItemHeight },
                      }}
                    />
                  )}
                  value={formData?.vendor}
                  onChange={(e, value) => handleInputChange('vendor', value)}
                  sx={{ height: formItemHeight }}
                />
              </Grid>
              <Grid item xs={12} marginTop={2}>
                <DialogContentText style={{ fontWeight: 'bold' }}>Vendor Item ID *</DialogContentText>
                <TextField
                  fullWidth
                  margin="dense"
                  size="small"
                  value={formData?.vendorItemID}
                  onChange={(e) => handleInputChange('vendorItemID', e.target.value)}
                  sx={{ height: formItemHeight }}
                  InputProps={{ sx: { height: formItemHeight } }}
              autoComplete="off"
                />
              </Grid>
            </Grid>
            <Grid container direction="row" justifyContent="space-between" alignItems="flex-end">
              <Grid item xs={12}>
                <DialogContentText style={{ fontWeight: 'bold' }}>UOM Amount *</DialogContentText>
                <TextField
                  fullWidth
                  margin="dense"
                  size="small"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={formData?.uomAmount}
                  onChange={(e) => handleInputChange('uomAmount', e.target.value)}
                  sx={{ height: formItemHeight }}
                  onKeyDown={(e) => fNoNegativeOrLetters(e)}
                  InputProps={{ sx: { height: formItemHeight } }}
              autoComplete="off"
                />
              </Grid>
              <Grid item xs={12}>
                <DialogContentText style={{ fontWeight: 'bold' }}>UOM Type *</DialogContentText>
                <Autocomplete
                  options={uomTypes}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      size="small"
                      sx={{ height: formItemHeight }}
                      InputProps={{
                        ...params.InputProps,
                        sx: { height: formItemHeight },
                      }}
                    />
                  )}
                  value={formData?.uomType}
                  onChange={(e, value) => handleInputChange('uomType', value)}
                  sx={{ height: formItemHeight, mb: 4 }}
              autoComplete="off"
                />
              </Grid>
            </Grid>
          </>
        )}
        {activeStep === 2 && (
          <>
            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ flex: 1 }}>
                <DialogContentText style={{ fontWeight: 'bold' }}>Main Pricing Type *</DialogContentText>
                <Autocomplete
                  options={pricingTypes}
                  getOptionLabel={(option) => option.name || ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      size="small"
                      sx={{ height: formItemHeight }}
                          autoComplete="off"
                      InputProps={{
                        ...params.InputProps,
                        sx: { height: formItemHeight },
                      }}
                    />
                  )}
                  value={pricingTypes?.find((f) => f.id === formData?.mainPricingType || null)}
                  onChange={(e, value) => {
                    setFormData({ ...formData, mainPricingType: value?.id });
                  }}
                  sx={{ height: formItemHeight }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <DialogContentText style={{ fontWeight: 'bold' }}>Main Price *</DialogContentText>
                <TextField
                  fullWidth
                  margin="dense"
                  size="small"
                  type="number"
                  value={formData?.mainPrice}
                  onChange={(e) => {
                    setFormData({ ...formData, mainPrice: e.target.value });
                  }}
                  sx={{ height: formItemHeight }}
                  InputProps={{ sx: { height: formItemHeight } }}
                  onKeyDown={(e) => fNoNegativeOrLetters(e)}
                      autoComplete="off"
                />
              </div>
            </div>
            {formData?.pricingTypes?.map((pricingType, index) => (
              <div key={index} style={{ display: 'flex', gap: '16px' }}>
                <div style={{ flex: 1 }}>
                  <DialogContentText style={{ fontWeight: 'bold' }}>Pricing Type</DialogContentText>
                  <Autocomplete
                    options={pricingTypes}
                    getOptionLabel={(option) => option.name || ''}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        margin="dense"
                        size="small"
                        sx={{ height: formItemHeight }}
                        InputProps={{
                          ...params.InputProps,
                          sx: { height: formItemHeight },
                        }}
                            autoComplete="off"
                      />
                    )}
                    value={pricingTypes?.find((f) => f.id === pricingType.pricingType)}
                    onChange={(e, value) => updatePricingType(index, 'pricingType', value.id)}
                    sx={{ height: formItemHeight }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <DialogContentText style={{ fontWeight: 'bold' }}>Price </DialogContentText>
                  <TextField
                    fullWidth
                    margin="dense"
                    size="small"
                    value={pricingType.price}
                    onChange={(e) => updatePricingType(index, 'price', e.target.value)}
                    sx={{ height: formItemHeight }}
                    InputProps={{ sx: { height: formItemHeight } }}
                    type="number"
                    onKeyDown={(e) => fNoNegativeOrLetters(e)}
                        autoComplete="off"
                  />
                </div>
                <div style={{ marginTop: '26px' }}>
                  <IconButton
                    onClick={() => {
                      deletePricingType(index);
                    }}
                  >
                    <Iconify icon="material-symbols:delete-outline" width={20} height={20} color="error" />
                  </IconButton>
                </div>
              </div>
            ))}

            <Grid container direction="row" justifyContent="space-between" alignItems="center">
              <Button
                variant="text"
                color="primary"
                sx={{
                  textDecoration: 'underline',
                }}
                onClick={() =>
                  setFormData({
                    ...formData,
                    pricingTypes: [...formData?.pricingTypes, { pricingType: null, price: null }],
                  })
                }
              >
                Add additional pricing type
              </Button>
            </Grid>
            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ flex: 1 }}>
                <Tooltip title="Amount to pay until ownership">
                  <DialogContentText style={{ fontWeight: 'bold' }}>Cap Rate ($)</DialogContentText>
                </Tooltip>
                <TextField
                  fullWidth
                  margin="dense"
                  size="small"
                  value={formData?.capAmount}
                  onChange={(e) => handleInputChange('capAmount', formatMoney(e.target.value))}
                  sx={{ height: formItemHeight }}
                  InputProps={{ sx: { height: formItemHeight } }}
                  autoComplete="off"
                />
              </div>
              <div style={{ flex: 1 }}>
                <Tooltip title="Rental days until ownership">
                  <DialogContentText style={{ fontWeight: 'bold' }}>Days to Cap</DialogContentText>
                </Tooltip>
                <TextField
                  fullWidth
                  margin="dense"
                  type="number"
                  size="small"
                  value={formData?.daysToCap}
                  onChange={(e) => handleInputChange('daysToCap', e.target.value)}
                  sx={{ height: formItemHeight }}
                  InputProps={{ sx: { height: formItemHeight } }}
                  onKeyDown={(e) => fNoNegativeOrLetters(e)}
                  autoComplete="off"
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ flex: 1 }}>
                <DialogContentText style={{ fontWeight: 'bold' }}>Weight Limit</DialogContentText>
                <TextField
                  fullWidth
                  margin="dense"
                  size="small"
                  value={formData?.weightLimit}
                  onChange={(e) => handleInputChange('weightLimit', e.target.value)}
                  sx={{ height: formItemHeight }}
                  InputProps={{ sx: { height: formItemHeight } }}
                  autoComplete="off"
                />
              </div>
              <div style={{ flex: 1 }}>
                <DialogContentText style={{ fontWeight: 'bold' }}>Warranty</DialogContentText>
                <TextField
                  fullWidth
                  margin="dense"
                  size="small"
                  value={formData?.warranty}
                  onChange={(e) => handleInputChange('warranty', e.target.value)}
                  sx={{ height: formItemHeight }}
                  InputProps={{ sx: { height: formItemHeight } }}
                  autoComplete="off"
                />
              </div>
            </div>
            <DialogContentText style={{ fontWeight: 'bold' }}>Vendor Item Link</DialogContentText>
            <TextField
              fullWidth
              margin="dense"
              size="small"
              value={formData?.vendorItemLink}
              onChange={(e) => handleInputChange('vendorItemLink', e.target.value)}
              sx={{ height: formItemHeight }}
              InputProps={{ sx: { height: formItemHeight } }}
              autoComplete="off"
            />
            <DialogContentText style={{ fontWeight: 'bold' }}>Image URL</DialogContentText>
            <TextField
              fullWidth
              margin="dense"
              size="small"
              value={formData?.imageUrl}
              onChange={(e) => handleInputChange('imageUrl', e.target.value)}
              sx={{ height: formItemHeight }}
              InputProps={{ sx: { height: formItemHeight } }}
              autoComplete="off"
            />
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          padding: '16px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {activeStep > 0 && (
          <Button onClick={handleBack} color="primary" variant="contained">
            Previous
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button onClick={handleNext} color="primary" variant="contained" disabled={!isStepValid}>
            Next
          </Button>
        ) : (
          <Button
            onClick={handleAddItem}
            variant="contained"
            color="primary"
            sx={{ boxShadow: 3, fontWeight: 'bold' }}
            disabled={!isStepValid}
          >
            Add Item
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
